import { useState } from 'react';
import { cx } from '../utils';

const DEFAULT_AVATAR_SIZE = 'h-8 w-8';
const DEFAULT_INITIALS_SIZE = 'text-[11px]';

/** "Ada Lovelace" -> "AL", "prisma" -> "PR", "" -> "?". */
function initialsOf(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return '?';
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export interface CellAvatarProps {
    src?: string | null;
    /** Used only for the initials fallback; the image itself is decorative. */
    name: string;
    /** Size/ring overrides. Defaults to `'h-8 w-8'`. */
    className?: string;
    /** Type size of the initials fallback, for avatars larger than a table cell's. */
    initialsClassName?: string;
}

/**
 * Avatar image with an initials fallback, in the design system's yellow.
 *
 * A broken URL is a runtime fact, not a data one, so the fallback is driven by
 * the image's own `onError` rather than by the caller pre-validating the URL.
 * Lives in its own file so `avatarColumn`'s module exports factories only
 * (react-refresh/only-export-components).
 */
export function CellAvatar({
    src,
    name,
    className,
    initialsClassName = DEFAULT_INITIALS_SIZE
}: CellAvatarProps) {
    const [failed, setFailed] = useState(false);
    const shell = cx(
        'shrink-0 rounded-full ring-2 ring-gray-200 aspect-square overflow-hidden',
        className ?? DEFAULT_AVATAR_SIZE
    );
    if (!src || failed) {
        return (
            <span
                aria-hidden
                className={cx(
                    shell,
                    'flex items-center justify-center bg-yellow-400 text-gray-600 font-bold',
                    initialsClassName
                )}
            >
                {initialsOf(name)}
            </span>
        );
    }
    return (
        <span className={shell}>
            <img
                src={src}
                alt=""
                loading="lazy"
                onError={() => setFailed(true)}
                className="h-full w-full object-cover"
            />
        </span>
    );
}
